'use client';

import type { ComponentProps, CSSProperties, PropsWithChildren } from 'react';

import { usePathname } from 'next/navigation';

import { MobileBottomTabBar } from '~/(tenant)/_components/native/mobile-bottom-tab-bar';
import type { TabsConfig } from '~/(tenant)/_lib/schema/native-home-config.schema';

import { NativeAuthLayoutShell } from '@kit/auth/native';

import { useNativeBack, useNativeNavigationScope } from '~/(tenant)/_components/native/native-navigation';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { deriveContrastingForeground } from '~/lib/utils/color-derivation';

/** Shares the booking screen's native tokens with the reusable auth package. */
export function NativeAuthTheme({ children }: PropsWithChildren) {
  const tokens = useNativeTokens();
  const style: CSSProperties & Record<`--${string}`, string> = {
    '--native-auth-accent': tokens.accent,
    '--native-auth-accent-foreground': deriveContrastingForeground(tokens.accent),
    '--native-auth-foreground': tokens.foreground,
    '--native-auth-muted': tokens.muted,
    '--native-auth-line': tokens.line,
    '--native-auth-radius': `${tokens.radius}px`,
    fontFamily: tokens.fontSans,
  };

  return <div style={style}>{children}</div>;
}

export function NativeAuthNavigationShell({
  tenantId,
  tabsEnabled = false,
  tabsConfig,
  ...props
}: ComponentProps<typeof NativeAuthLayoutShell> & {
  tenantId?: string;
  tabsEnabled?: boolean;
  tabsConfig?: TabsConfig;
}) {
  const onBack = useNativeBack();
  const pathname = usePathname();
  const showTabs = tabsEnabled && (
    pathname === '/auth/sign-in' || pathname === '/auth/sign-up'
  );
  useNativeNavigationScope(tenantId);

  return (
    <>
      <NativeAuthLayoutShell {...props} onBack={onBack} hasBottomTabs={showTabs} />
      {showTabs && <MobileBottomTabBar enabled tabsConfig={tabsConfig} />}
    </>
  );
}
